/* eslint-disable no-console */
/**
 * Debug Logging Utility
 *
 * Gates console logs based on environment to prevent logging in production.
 * Only logs in development/localhost environments.
 *
 * SECURITY NOTES:
 * - All logs go to browser DevTools Console (F12 → Console tab)
 * - In production, debug logs are suppressed to prevent information leakage
 * - Errors are always logged (even in production) for debugging purposes
 * - Never log sensitive data (passwords, tokens, PII) even in development
 *
 * HOW TO VIEW LOGS:
 * 1. Open browser DevTools (F12 or Right-click → Inspect)
 * 2. Go to "Console" tab
 * 3. Logs appear in real-time as the app runs
 * 4. Filter by log level: All, Errors, Warnings, Info, Logs
 */

/**
 * Check if we're in development mode
 *
 * Detection priority:
 * 1. REACT_APP_ENVIRONMENT (set at build time, most reliable)
 * 2. NODE_ENV (for server-side rendering)
 * 3. Hostname check (fallback, less secure but useful for localhost)
 *
 * @returns {boolean} True if in development or localhost
 */
export const isDevelopment = () => {
  // Server-side rendering check
  if (typeof window === 'undefined') {
    // Server-side: use NODE_ENV
    return process.env.NODE_ENV !== 'production';
  }

  // Client-side: prioritize build-time environment variable (most secure)
  // This is set during `npm run build` in GitHub Actions
  const envFromBuild = process.env.REACT_APP_ENVIRONMENT;

  // If explicitly set to 'production' at build time, respect it (most secure)
  if (envFromBuild === 'production') {
    return false;
  }

  // If explicitly set to 'development' or 'staging', allow logging
  if (envFromBuild === 'development' || envFromBuild === 'staging') {
    return true;
  }

  // Fallback: Check hostname (less secure, but useful for localhost)
  // WARNING: Hostname can be spoofed, so this is only a fallback
  const hostname = window.location.hostname;
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0'
  ) {
    return true;
  }

  // Default: if REACT_APP_ENVIRONMENT is not set, assume development
  // This is safer than assuming production (prevents accidental log suppression)
  return envFromBuild !== 'production';
};

/**
 * Debug log - only logs in development
 *
 * @param {...any} args - Arguments to log
 *
 * @example
 * debugLog('User logged in:', userId);
 * debugLog('API response:', { status: 200, data: response });
 */
export const debugLog = (...args) => {
  if (isDevelopment()) {
    console.log('[DEBUG]', ...args);
  }
};

/**
 * Debug warn - only logs in development
 *
 * @param {...any} args - Arguments to log
 *
 * @example
 * debugWarn('Deprecated API endpoint used:', endpoint);
 */
export const debugWarn = (...args) => {
  if (isDevelopment()) {
    console.warn('[WARN]', ...args);
  }
};

/**
 * Debug error - always logs (errors should be visible in production too)
 *
 * SECURITY WARNING: This always logs, even in production.
 * Be careful not to log sensitive information (passwords, tokens, PII).
 *
 * @param {...any} args - Arguments to log
 *
 * @example
 * debugError('API request failed:', error);
 * // ❌ BAD: debugError('User password:', password);
 * // ❌ BAD: debugError('API token:', token);
 */
export const debugError = (...args) => {
  // Errors are always logged, even in production
  // This helps with debugging production issues
  console.error('[ERROR]', ...args);
};

/**
 * Debug info - only logs in development
 *
 * @param {...any} args - Arguments to log
 *
 * @example
 * debugInfo('Component mounted:', componentName);
 */
export const debugInfo = (...args) => {
  if (isDevelopment()) {
    console.info('[INFO]', ...args);
  }
};

/**
 * Sanitize sensitive data before logging
 *
 * Use this helper to redact sensitive information before logging
 *
 * @param {any} data - Data to sanitize
 * @param {string[]} sensitiveKeys - Keys to redact (default: common sensitive fields)
 * @returns {any} Sanitized data
 *
 * @example
 * const userData = { email: 'user@example.com', password: 'secret123' };
 * debugLog('User data:', sanitizeForLogging(userData));
 * // Output: { email: 'user@example.com', password: '[REDACTED]' }
 */
export const sanitizeForLogging = (
  data,
  sensitiveKeys = [
    'password',
    'token',
    'apiKey',
    'secret',
    'authorization',
    'auth',
  ]
) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeForLogging(item, sensitiveKeys));
  }

  const sanitized = { ...data };
  for (const key of sensitiveKeys) {
    if (key in sanitized) {
      sanitized[key] = '[REDACTED]';
    }
  }

  return sanitized;
};
